/**
 * useTheme — resolves the theme choice onto <html data-theme> (brief-28).
 *
 * 'system' is the default and follows prefers-color-scheme live, so a machine
 * that flips to dark at sunset takes the app with it without a reload. An
 * explicit 'light' or 'dark' pins it and stops listening.
 *
 * The attribute lives on <html> rather than a React wrapper so that portals,
 * the scrollbar and color-scheme all inherit it.
 */

import { useEffect, useState } from 'react';
import type { ThemeChoice } from './useCatalogPrefs';

const QUERY = '(prefers-color-scheme: dark)';

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(QUERY).matches;
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean): 'light' | 'dark' {
  if (choice === 'system') return systemDark ? 'dark' : 'light';
  return choice;
}

export function useTheme(choice: ThemeChoice): 'light' | 'dark' {
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (choice !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    setSystemDark(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  const resolved = resolveTheme(choice, systemDark);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  return resolved;
}
